import { uuid } from "vue-uuid";
import matrixApi from "@shared/modules/work-allocation-matrix-admin/api";
import { set, cloneDeep } from "lodash";
import {
  defaultMatrixConfigFieldCheck,
  matrixConfigurationPropValueCheck,
  getMatrixConfigurationValidators,
} from ".";
import {
  REQUIRED_FIELDS,
  REQUIRED_CONFIG_FIELDS,
  OPTIONAL_CONFIG_FIELDS,
} from "./constants";

/**
 * @typedef {Object} WAMData
 * @property {string} id - Unique identifier for the matrix item.
 * @property {string} name - Name of the matrix item.
 * @property {string} description - Description of the matrix item.
 * @property {string} created_at - Creation timestamp of the matrix item.
 * @property {string} created_by - User who created the matrix item.
 * @property {string} modified_at - Last modification timestamp of the matrix item.
 * @property {string} modified_by - User who last modified the matrix item.
 * @property {WAMConfig} config - List of configs in the matrix.
 */

/** 
 * @typedef {Object} WAMConfig
 * @property {string} id - Unique identifier for the config.
 * @property {Array<string>} clients - List of client IDs associated with the config.
 * @property {Array<string>} users - List of user IDs associated with the config.
 * @property {Array<string>} groups - List of group IDs associated with the config.
 * @property {WAMConfigurationProp} configuration - Configuration settings for the matrix.
 * @property {String} action - Action settings for the matrix.
 */

/**
 * @typedef {Object} WAMConfigurationProp
 * @property {string} id - Unique identifier for the configuration.
 * @property {Array<string>} value - List of values associated with the configuration.
 */

export class MatrixData {
  /**
   * @param {WAMData} payload
   */
  constructor(payload = null) {
    this.setData(payload || {});
    this.isLoading = false;
    this.error = null;
    this.isGeneralSaved = true;
    this.isConfigSaved = true;
    this.isSaved = true;
    this._configValidators = getMatrixConfigurationValidators(this);

    // If payload is provided, set general saved state to true
    if (payload) {
      this.setSaved(true);
    }
  }

  transformPayload(payload) {
    const config = (payload.config || []).map(row => {
      const { configuration, ...rest } = row;
      return ({ ...rest, configuration, values: { ...configuration} });
    })
    return { ...payload, config } 
  } 

  // --- State setters/getters ---
  setData(payload) {
    if (!payload) {
      console.warn("Payload is null or undefined. Cannot set data.");
      return this;
    }
    if (typeof payload !== 'object') {
      console.warn("Payload is not an object. Cannot set data.");
      return this;
    }

    payload = this.transformPayload(payload); // adds missing values key

    this.data = {
      ...this.data,
      name: payload.name || '',
      description: payload.description || '',
      ...payload,
      config: payload.config?.map(config => {
        if (config instanceof MatrixConfig) {
          return config; // Already an instance
        }
        return new MatrixConfig(config); // Convert to MatrixConfig instance
      }) || [],
    };
    this.isLoading = false;
    this.error = null;
    return this;
  }
  setGeneralSaved(saved = true) {
    this.isGeneralSaved = saved;
    return this;
  }
  setConfigSaved(saved = true) {
    this.isConfigSaved = saved;
    return this;
  }
  setSaved(saved = true) {
    if (saved) {
      this.setGeneralSaved(saved);
      this.setConfigSaved(saved);
    }
    this.isSaved = saved;
    return this;
  }
  touch() {
    this.touchGeneral();
    this.touchConfig();
    this.setSaved(false);
    return this;
  }
  touchGeneral() {
    this.setGeneralSaved(false);
    this.setSaved(false);
    return this;
  }
  touchConfig() {
    this.setConfigSaved(false);
    this.setSaved(false);
    return this;
  }
  checkConfigErrors(config) {
    if (!config || !(config instanceof MatrixConfig)) {
      console.warn("Invalid config provided. Cannot validate.");
      return false;
    }
    if (!this._configValidators) {
      console.warn("No configuration validators found. Cannot validate config.");
      return true; // If no validators, consider it valid
    }
    const failedChecks = this._configValidators.filter(validator => {
      if (typeof validator.check !== 'function') {
        console.warn(`Validator ${validator.id} does not have a check function. Skipping validation.`);
        return true; // If no check function, consider it valid
      }
      const isCheckPassed = validator.check(config);
      if (!isCheckPassed) {
        console.warn(`Validation failed for ${validator.id}: ${validator.message}`);
      }
      return !isCheckPassed; // Return the result of the check
    });
    return failedChecks;
  }

  // --- CRUD methods ---
  async fetch() {
    this.isLoading = true;
    this.error = null;
    if (!this.data?.id) {
      console.warn("Matrix ID is not set. Cannot fetch data.");
      this.isLoading = false;
      return;
    }

    try {
      const response = await matrixApi.getWorkAllocationMatrixItem(this.data.id);
      this.data = response || null;
    } catch (error) {
      console.error("Error fetching matrix data:", error);
      this.error = error;
    } finally {
      this.isLoading = false;
    }
  }
  async save(generalOnly = false) {
    this.isLoading = true;
    this.error = null;
    if (!this.data) {
      console.warn("Matrix data is not set. Cannot save.");
      this.isLoading = false;
      return;
    }

    const { config, ...payload } =  this.getData;

    if (!generalOnly) {
      payload.config = config;
    }

    // Validate required fields
    if (!payload.name) {
      console.warn("Name is required. Cannot save matrix without a name.");
      this.isLoading = false;
      throw new Error("Name is required");
    }

    try {
      if (payload.id) {
        // Update existing matrix
        await matrixApi.putWorkAllocationMatrixItem(this.data?.id, payload);
      } else {
        // Create new matrix
        await matrixApi.postWorkAllocationMatrixItem(payload);
      }

      // Reset the changed state for all configurations
      this.data?.config.forEach(config => {
        config.setChanged(false);
      });
      this.setSaved();
    } catch (error) {
      console.error("Error saving matrix data:", error);
      this.error = error;
      throw error; // Re-throw to be caught by the caller
    } finally {
      this.isLoading = false;
    }

    return this;

    // TODO refetch/override data with the response
  }

  // --- Config management ---
  addNewConfiguration(payload = null, clone = false) {
    if (!this.data) {
      console.warn("Matrix data is not set. Cannot add configuration.");
      return;
    }

    const newMatrixConfig = new MatrixConfig(payload, clone);
    newMatrixConfig.setEditing(); // Set editing
    this.touchConfig(); // Mark as changed

    // If there is a source row inser the clone right after original:
    if (payload?.id) {
      const sourceIndex = this.data.config.findIndex(config => config?.data?.id === payload.id);
      if (sourceIndex !== -1) {
        this.data.config.splice(sourceIndex + 1, 0, newMatrixConfig);
        return newMatrixConfig; // Return the newly added config
      }
    }

    this.data.config.push(newMatrixConfig);

    return newMatrixConfig;
  }
  removeConfiguration(configId) {
    if (!this.data) {
      console.warn("Matrix data is not set. Cannot remove configuration.");
      return;
    }

    this.data.config = this.data.config.filter(config => config?.data?.id !== configId);
    this.touchConfig(); // Mark as changed

    return this;
  }

  // --- Computed properties ---
  get isAnyEditing() {
    if (!this.data || !this.data.config) {
      return false;
    }
    return this.data.config.some(config => config.isEditing);
  }
  get getData() {
    if (!this.data) {
      console.warn("Matrix data is not set. Cannot get data.");
      return null;
    }
    const configsData = this.data.config.map(config => {
      if (config instanceof MatrixConfig) {
        const configData = config.getData;

        // Delete ID property as we save the new configurations set:
        if (configData?.id)
          delete configData.id;

        return configData; // Return the data of the MatrixConfig instance
      }
      console.warn("Invalid config instance in matrix data.");
      return config; // Return as is if not an instance
    });

    return {
      ...this.data,
      config: configsData.filter(Boolean), // Filter out any null or undefined configs
    };
  }
  get isValid() {
    if (!this.data) {
      console.warn("Matrix data is not set. Cannot validate.");
      return false;
    }
    // Check if all required fields have values
    const hasRequiredFields = REQUIRED_FIELDS.every(field => this.data[field]);
    return hasRequiredFields && this.data.config.every(config => config.isValid);
  }
}

export class MatrixConfig {
  /**
   * @param {WAMConfig} payload
   */
  constructor(payload, clone = false) {
    this.setData(payload, clone);
    this.isLoading = false;
    this.error = null;
    this.isEditing = false;
    this.isChanged = true;
  }

  // --- State setters/getters ---
  setData(payload = null, clone = false) {
    if (payload && typeof payload !== 'object') {
      console.warn("Invalid payload provided. Cannot set data.");
      return this;
    }
    const {
      id,
      clients = [],
      users = [],
      groups = [],
      values = null,
      configuration = null,
      action = null,
    } = cloneDeep(payload || {});

    const configurationValue = values
      ? (configuration instanceof MatrixConfigurationProp ? configuration : new MatrixConfigurationProp(configuration)) // Convert to MatrixConfigurationProp instance if provided
      : null; // Default to null if no configuration provided

    const valuesValue = values
      ? (values instanceof MatrixConfigurationProp ? values : new MatrixConfigurationProp(values)) // Convert to MatrixConfigurationProp instance if provided
      : null; // Default to null if no configuration provided

    this.data = {
      id: (clone || !id) ? uuid.v4() : id, // Generate a new UUID if cloning or no ID provided
      clients: Array.isArray(clients) ? clients : [],
      users: Array.isArray(users) ? users : [],
      groups: Array.isArray(groups) ? groups : [],
      values: valuesValue,
      configuration: configurationValue,
      action: action || null, // Ensure action is set
    };
  }
  setEditing(editing = true) {
    this.isEditing = editing;
    return this;
  }
  setChanged(changed = true) {
    this.isChanged = changed;
    return this;
  }
  touch() {
    this.setChanged(true);
    return this;
  }

  // --- Field setters ---
  setValue(field, value) {
    if (!this.data) {
      console.warn("Matrix config data is not set. Cannot set field value.");
      return this;
    }
    if (!field || typeof field !== 'string') {
      console.warn("Invalid field name provided. Cannot set field value.");
      return this;
    }
    // Use lodash's set to safely set the value
    set(this.data, field, value);
    this.touch(); // Mark as changed
    return this;
  }

  // --- Computed properties ---
  get getData() {
    if (!this.data) {
      console.warn("Matrix config data is not set. Cannot get data.");
      return null;
    }

    // Ensure configuration is in the correct format
    const configurationData = this.data.configuration instanceof MatrixConfigurationProp
      ? this.data.configuration.getData
      : this.data.configuration;

    return {
      ...this.data,
      configuration: configurationData,
    };
  }
  
  // --- State getters ---
  get isValid() {
    if (!this.data) {
      console.warn("Matrix config data is not set. Cannot validate.");
      return false;
    }

    // Check if all required fields have values
    return this._hasRequiredFields && this._hasOneOfOptionalField && this._isConfigurationValid;
  }

  // Change if need to set different conditions for disabling
  get isDisabled() {
    return !this._hasRequiredFields || !this._hasOneOfOptionalField;
  }

  // --- Configuration validation ---
  get _isConfigurationValid() {
    const configuration = this.data?.configuration;

    return !configuration
      || (configuration instanceof MatrixConfigurationProp && matrixConfigurationPropValueCheck(configuration));
  }

  get _hasRequiredFields() {
    return REQUIRED_CONFIG_FIELDS.every(field => {
      return defaultMatrixConfigFieldCheck(this.getData[field]);
    });
  }

  get _hasOneOfOptionalField() {
    return OPTIONAL_CONFIG_FIELDS.some(field => {
      return defaultMatrixConfigFieldCheck(this.getData[field]);
    });
  }
}

export class MatrixConfigurationProp {
  /**
   * @param {WAMConfigurationProp} payload
   */
  constructor(payload = null) {
    const { id = null, values = null } = payload || {};
    this.data = {
      id,
      values,
      type: payload?.type || "FIELD",
    };
    this.isLoading = false;
    this.error = null;
    this.options = [];
    this.isMulti = false;
  }

  // --- State setters/getters ---
  setLoading(loading = true) {
    this.isLoading = loading;
    return this;
  }
  setIsMulti(isMulti = false) {
    this.isMulti = isMulti;
    return this;
  }
  setOptions(options = []) {
    if (!Array.isArray(options)) {
      console.warn("Options should be an array. Cannot set options.");
      return this;
    }
    this.options = options;
    return this;
  }

  // --- Value setters ---
  setValue(value = []) {
    if (!this.data) {
      console.warn("Matrix configuration data is not set. Cannot set value.");
      return this;
    }
    if (value && !Array.isArray(value)) {
      // Ensure value is always an array since API only allows arrays
      this.data.values = [value];
    } else {
      this.data.values = value;
    }
    return this;
  }

  // --- Async ---
  async fetchOptions(apiCallMethod) {
    this.setLoading(true);
    this.error = null;

    if (!apiCallMethod || typeof apiCallMethod !== 'function') {
      console.warn("Invalid API call method provided. Cannot fetch options.");
      this.setLoading(false);
      return this;
    }

    try {
      const response = await apiCallMethod();
      this.setOptions(response);
    } catch (error) {
      console.error("Error fetching configuration options:", error);
      this.error = error;
    } finally {
      this.setLoading(false);
    }

    return this;
  }

  // --- Computed properties ---
  get getData() {
    if (!this.data?.id) {
      return null;
    }
    return this.data;
  }

  get getValues() {
    if (!this.data?.values) {
      return null;
    }
    return this.data.values;
  }
  get getMultiselectValue() {
    if (!this.data?.values) {
      return null;
    }
    if (!this.isMulti) {
      return this.data.values[0] || null;
    } else {
      return this.data.values;
    }
  }
}